import React, { memo, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Container } from '../../layout'
import type { NavTabItemProps } from '../NavTabs/NavTabItem/NavTabItem'
import NavTabs from '../NavTabs/NavTabs'
import type { SubNavTabItemProps } from '../SubNavTabs/SubNavTabItem/SubNavTabItem'
import SubNavTabs from '../SubNavTabs/SubNavTabs'
import { SubNavContainer } from './Navigation.styles'
import type { NavigationTitleProps, TitleProps } from './NavigationTitle/NavigationTitle'
import NavigationTitle from './NavigationTitle/NavigationTitle'

const pathnameMatchesLink = (pathname: string, link: string) =>
  pathname === link || pathname.startsWith(`${link}/`)

export const isTabActive = (tab: NavTabProps, pathname: string) =>
  pathnameMatchesLink(pathname, tab.link) ||
  !!tab.subNav?.some(nav => pathnameMatchesLink(pathname, nav.link))

export const getActiveTab = (tabs: NavTabProps[] = [], pathname: string = '') =>
  tabs.find(tab => isTabActive(tab, pathname))

export const getActiveSubTab = (tab: NavTabProps | undefined, pathname: string) =>
  tab?.subNav?.find(nav => pathnameMatchesLink(pathname, nav.link))

export type SubNavTabProps = SubNavTabItemProps & {
  actions?: NavigationTitleProps['actions']
  description?: NavigationTitleProps['description']
  subtitle?: string
}

export type NavTabProps = NavTabItemProps & {
  description?: NavigationTitleProps['description']
  subNav?: SubNavTabProps[]
  subtitle?: string
  topContent?: React.ReactNode
}

export interface NavigationProps {
  actions?: NavigationTitleProps['actions']
  beta?: boolean
  'data-e2e'?: string
  description?: NavigationTitleProps['description']
  fluid?: boolean
  onBackLinkClick?: () => void
  onClick?: () => void
  tabs?: NavTabProps[]
  title?: TitleProps
}

const Navigation = ({
  actions,
  beta,
  'data-e2e': dataE2e,
  tabs,
  title,
  description,
  onClick,
  onBackLinkClick,
  fluid,
}: NavigationProps) => {
  const { pathname, search } = useLocation()
  const history = useHistory()
  const [activeTab, setActiveTab] = useState(getActiveTab(tabs, pathname))
  const [activeSubTab, setActiveSubTab] = useState(getActiveSubTab(activeTab, pathname))

  const subtitle = activeSubTab?.subtitle || activeTab?.subtitle || title
  const currentDescription = activeSubTab?.description || activeTab?.description || description

  useEffect(() => {
    const newActiveTab = getActiveTab(tabs, pathname)
    const newActiveSubTab = getActiveSubTab(newActiveTab, pathname)

    if (newActiveTab?.subNav?.length && !newActiveSubTab) {
      history.push(`${newActiveTab.subNav[0].link}${search}`)
    } else {
      setActiveTab(newActiveTab)
      setActiveSubTab(newActiveSubTab)
    }
  }, [pathname])

  return (
    <div data-e2e={dataE2e}>
      {!!tabs && (
        <NavTabs
          tabs={tabs.map(tab => ({ ...tab, isActive: isTabActive(tab, pathname) }))}
          onClick={onClick}
        />
      )}

      <SubNavContainer>
        <Container fluid={fluid}>
          {activeTab?.topContent}

          {!!activeTab?.subNav && (
            <SubNavTabs hasTitle={!!subtitle} tabs={activeTab.subNav} onClick={onClick} />
          )}

          {!!subtitle && (
            <NavigationTitle
              actions={activeSubTab?.actions ?? actions}
              beta={beta}
              description={currentDescription}
              onBackLinkClick={onBackLinkClick}
              title={subtitle}
            />
          )}
        </Container>
      </SubNavContainer>
    </div>
  )
}

export default memo(Navigation)
